// src/components/staff/mobile/MobileLiveQueue.jsx
// Utilise la vraie route /staff/client-requests (source: ClientRequestsView.jsx)
// Visible to: Dispatcher (25+), Supervisor (75+), Admin/Manager (90+)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Fuel, Wrench, User, RefreshCw, Clock, Check, XCircle, Zap, CheckCircle } from 'lucide-react';
import { getAllClientRequests, approveClientRequest, rejectClientRequest, getStaffList } from '../../../services/staffApi';

const GLOBAL_STYLE = `
  @keyframes spin { to { transform: rotate(360deg) } }
  @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
`;

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2 };
const PRIORITY_ACCENT = { urgent: '#ef4444', high: '#fb923c', normal: '#64748b' };

const sortByUrgency = (list) =>
  [...list].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority] ?? 2;
    const pb = PRIORITY_ORDER[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    return new Date(a.created_at) - new Date(b.created_at);
  });

const UrgencyDot = ({ priority }) => (
  <div style={{
    width: 8, height: 8, borderRadius: 4, flexShrink: 0,
    background: PRIORITY_ACCENT[priority] ?? '#64748b',
    animation: priority === 'urgent' ? 'pulse 1s infinite' : 'none',
  }} />
);

// ─── Queue Card ───────────────────────────────────────────────────────────────
const QueueCard = ({ item, onRefresh }) => {
  const [saving, setSaving] = useState(false);

  const isFuel = item.request_type === 'fuel';
  const accent = isFuel ? '#fb923c' : '#f87171';
  const Icon = isFuel ? Fuel : Wrench;
  const priority = item.priority ?? 'normal';
  const pAccent = PRIORITY_ACCENT[priority] ?? '#64748b';

  const timeAgo = (s) => {
    const diff = Date.now() - new Date(s);
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return `${Math.floor(diff / 3600000)}h ago`;
  };

  const handleApprove = async () => {
    setSaving(true);
    try { await approveClientRequest(item.id); onRefresh(); }
    catch { alert('Error approving'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      background: '#1e293b', borderRadius: 18,
      border: `0.5px solid ${priority === 'urgent' ? 'rgba(239,68,68,0.4)' : '#334155'}`,
      overflow: 'hidden',
      boxShadow: priority === 'urgent' ? '0 0 0 1px rgba(239,68,68,0.15)' : 'none',
    }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={18} color={accent} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <UrgencyDot priority={priority} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {isFuel ? `${item.quantity || '?'} gal — ${item.tail_number || ''}` : (item.tail_number || 'Maintenance')}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: `${pAccent}18`, color: pAccent, flexShrink: 0 }}>
                {priority.toUpperCase()}
              </span>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.client_first_name} {item.client_last_name}
              {item.company_name ? ` · ${item.company_name}` : ''}
            </p>
            {item.description && (
              <p style={{ fontSize: 11, color: '#475569', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 4 }}>
                {item.description}
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={11} color="#475569" />
              <span style={{ fontSize: 11, color: '#475569' }}>{timeAgo(item.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Approve button */}
        <div style={{ marginTop: 12 }}>
          <button onClick={handleApprove} disabled={saving} style={{
            width: '100%', padding: '11px', borderRadius: 12,
            background: 'rgba(34,197,94,0.15)', border: '0.5px solid rgba(34,197,94,0.3)',
            color: '#22c55e', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}>
            {saving
              ? <div style={{ width: 14, height: 14, border: '2px solid rgba(34,197,94,0.3)', borderTopColor: '#22c55e', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              : <Check size={14} />
            }
            {saving ? 'Processing…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileLiveQueue = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await getAllClientRequests({ status: 'pending' });
      const all = r.data.requests || [];
      setRequests(sortByUrgency(all));
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const urgentCount = requests.filter(r => r.priority === 'urgent').length;

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Status bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: '#22c55e', animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Live · {requests.length} pending</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#334155' }}>
            {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button onClick={load} style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 10, padding: '7px 9px', cursor: 'pointer' }}>
            <RefreshCw size={14} color="#94a3b8" />
          </button>
        </div>
      </div>

      {/* Urgent banner */}
      {urgentCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '0.5px solid rgba(239,68,68,0.3)', borderRadius: 14, marginBottom: 14 }}>
          <Zap size={15} color="#ef4444" />
          <span style={{ fontSize: 13, color: '#fca5a5', fontWeight: 600 }}>
            {urgentCount} urgent request{urgentCount !== 1 ? 's' : ''} need immediate attention
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <CheckCircle size={36} color="#22c55e" style={{ marginBottom: 12 }} />
          <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>All clear!</p>
          <p style={{ color: '#475569', fontSize: 13 }}>No pending requests right now</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map(req => <QueueCard key={req.id} item={req} onRefresh={load} />)}
        </div>
      )}
    </div>
  );
};

export default MobileLiveQueue;